import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * A custom Angular pipe that formats date values.
 *
 * This pipe displays "Today" for the current date and formats other dates
 * using a standard date format (e.g., "Feb 15, 2026").
 *
 * ### Example usage in template:
 * ```html
 * <span>{{ group.label | dateFormat }}</span>
 * ```
 */
@Pipe({
  name: 'dateFormat',
  standalone: true,
})
export class DateFormatPipe implements PipeTransform {
  private _datePipe: DatePipe;

  constructor() {
    this._datePipe = new DatePipe('en-US');
  }

  /**
   * Transforms a date value into a formatted string.
   *
   * @param value - The date value to format (Date object, ISO string, or timestamp).
   * @param format - Optional custom date format (default: 'MMMM d, y').
   * @returns 'Today' for the current date, or a formatted date string for other dates.
   */
  transform(value: Date | string | number | null | undefined, format: string = 'EEEE, MMM d'): string {
    if (!value) {
      return '';
    }

    const inputDate = new Date(value);
    const today = new Date();

    // Reset time to midnight for accurate date comparison
    const inputDateMidnight = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Check if the date is today
    if (inputDateMidnight.getTime() === todayMidnight.getTime()) {
      return 'Today';
    }

    // Format the date using Angular's DatePipe
    return this._datePipe.transform(value, format) || '';
  }
}
